import { useState, type ChangeEvent } from "react";
import Download from "@mui/icons-material/Download";
import Visibility from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { dayjs } from "@thalamos/common";
import { isUnconfirmedEstimatedPaymentRequest } from "@thalamos/aspire-common";
import type {
  Section12PaymentRequestStatus,
  Section12PaymentRequestWithDetails,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import {
  buildCsvFileName,
  downloadCsvBlob,
  formatCurrency,
  formatDoctorDisplayName,
  formatPaymentDate,
  STATUS_CHIP_SX,
  STATUS_COLORS,
  STATUS_LABELS_ADMIN,
  UNKNOWN_LOCAL_AUTHORITY_LABEL,
} from "./payment-request-helpers.js";
import { PaymentRequestDetailDialog } from "./PaymentRequestDetailDialog.js";
import {
  LoadingSpinner,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

export function PaymentReviewerPaymentRequestsPage() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<
    Section12PaymentRequestStatus | ""
  >("");
  const [fromDate, setFromDate] = useState<string>(
    dayjs().subtract(30, "days").format("YYYY-MM-DD"),
  );
  const [toDate, setToDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [selectedRequest, setSelectedRequest] =
    useState<Section12PaymentRequestWithDetails | null>(null);

  const [{ data, loading }] =
    apiHooks.section12.getPaymentRequestsByLocalAuthorities({
      status: statusFilter || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    });

  const handleExportCsv = async () => {
    try {
      const response =
        await api.section12.exportPaymentRequestsByLocalAuthoritiesCsv({
          status: statusFilter || undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        });
      const blob = new Blob([response.data], { type: "text/csv" });
      const fileName = buildCsvFileName(
        "payment-requests-by-la",
        fromDate || undefined,
        toDate || undefined,
      );
      downloadCsvBlob(blob, fileName);
      renderSuccessToast({ message: "CSV downloaded successfully" });
    } catch (error) {
      console.error("Failed to export CSV:", error);
      renderErrorToast({ message: "Failed to export CSV" });
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && !data) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <LoadingSpinner label="Loading payment requests..." />
      </Box>
    );
  }

  const paymentRequests = data?.requests ?? [];
  const totalCount = data?.count ?? 0;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Payment Requests
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 2,
        }}
      >
        Payment requests for your assigned local authorities.
      </Typography>
      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(
                e.target.value as Section12PaymentRequestStatus | "",
              );
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending AMHP approval</MenuItem>
            <MenuItem value="accepted">Approved by AMHP</MenuItem>
            <MenuItem value="rejected">Rejected by AMHP</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="payment-processing">Payment Processing</MenuItem>
            <MenuItem value="payment-on-hold">Payment on hold</MenuItem>
            <MenuItem value="receipt-missing">Receipt Missing</MenuItem>
            <MenuItem value="bank-information-missing">
              Bank Information Missing
            </MenuItem>
            <MenuItem value="value-incorrect">Value Incorrect</MenuItem>
            <MenuItem value="registration-with-icb-needed">
              Registration with ICB needed
            </MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="From Date"
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => {
            setFromDate(e.target.value);
            setPage(0);
          }}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />

        <TextField
          label="To Date"
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => {
            setToDate(e.target.value);
            setPage(0);
          }}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />

        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={() => {
            void handleExportCsv();
          }}
          size="small"
        >
          Export CSV
        </Button>
      </Box>
      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Reference</TableCell>
              <TableCell>Doctor</TableCell>
              <TableCell>AMHP Team</TableCell>
              <TableCell>Local Authority</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paymentRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No payment requests found
                </TableCell>
              </TableRow>
            ) : (
              paymentRequests.map(
                (request: Section12PaymentRequestWithDetails) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {request.paymentReference}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {request.doctor
                        ? formatDoctorDisplayName(
                            request.doctor.name,
                            request.doctor.teamName,
                            request.doctor.teamType,
                          )
                        : "Unknown Doctor"}
                    </TableCell>
                    <TableCell>
                      {request.amhpTeam?.name ?? "Unknown Team"}
                    </TableCell>
                    <TableCell>
                      {request.localAuthority?.name ??
                        UNKNOWN_LOCAL_AUTHORITY_LABEL}
                    </TableCell>
                    <TableCell align="right">
                      {isUnconfirmedEstimatedPaymentRequest(request)
                        ? "Estimated"
                        : formatCurrency(request.totalAmountPence)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS_ADMIN[request.status]}
                        color={STATUS_COLORS[request.status]}
                        sx={STATUS_CHIP_SX[request.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatPaymentDate(request.created)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedRequest(request);
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ),
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <PaymentRequestDetailDialog
        request={selectedRequest}
        onClose={() => {
          setSelectedRequest(null);
        }}
      />
    </Box>
  );
}
